import { resolveAssetUrl } from './mediaStore';
import type { AudioSettings } from '@cinegrid/core';
import { resolveAudioSettings } from '../engine/audioFx';

type PeakData = {
  /** Interleaved min,max in [-1,1], length = buckets * 2 */
  peaks: Float32Array;
  buckets: number;
  duration: number;
};

export type WaveformFxStyle = {
  volume: number;
  noiseRemoval: number;
  eqGainDb: number;
  compressor: { enabled: boolean; thresholdDb: number; ratio: number } | null;
  reverb: number;
};

const cache = new Map<string, PeakData>();
const inflight = new Map<string, Promise<PeakData | null>>();

async function decodePeaks(
  assetId: string,
  url: string,
  buckets: number
): Promise<PeakData | null> {
  try {
    const res = await fetch(url);
    const buf = await res.arrayBuffer();
    const ctx = new AudioContext();
    const audio = await ctx.decodeAudioData(buf.slice(0));
    await ctx.close();

    const ch = audio.getChannelData(0);
    const duration = audio.duration || 1;
    const peaks = new Float32Array(buckets * 2);
    const block = Math.max(1, Math.floor(ch.length / buckets));

    for (let i = 0; i < buckets; i++) {
      const start = i * block;
      const end = Math.min(ch.length, start + block);
      let min = 1;
      let max = -1;
      for (let j = start; j < end; j++) {
        const v = ch[j];
        if (v < min) min = v;
        if (v > max) max = v;
      }
      if (end <= start) {
        min = 0;
        max = 0;
      }
      peaks[i * 2] = min;
      peaks[i * 2 + 1] = max;
    }

    const data: PeakData = { peaks, buckets, duration };
    cache.set(assetId, data);
    return data;
  } catch (err) {
    console.warn('Waveform decode failed', assetId, err);
    return null;
  }
}

export async function getWaveformPeaks(
  asset: { id: string; url: string },
  buckets = 1000
): Promise<PeakData | null> {
  const cached = cache.get(asset.id);
  if (cached) return cached;

  let p = inflight.get(asset.id);
  if (!p) {
    p = resolveAssetUrl(asset)
      .then((url) => decodePeaks(asset.id, url, buckets))
      .finally(() => inflight.delete(asset.id));
    inflight.set(asset.id, p);
  }
  return p;
}

/** Build draw style from clip audio settings + opacity. */
export function waveformStyleFromAudio(
  audio: AudioSettings | undefined,
  opacity = 1
): WaveformFxStyle {
  const a = resolveAudioSettings(audio);
  const eqGainDb = a.eq.enabled
    ? (a.eq.low + a.eq.mid * 1.25 + a.eq.high) / 3.25
    : 0;
  return {
    volume: Math.max(0, a.volume * opacity),
    noiseRemoval: a.noiseRemoval.enabled ? a.noiseRemoval.amount : 0,
    eqGainDb,
    compressor: a.compressor.enabled
      ? {
          enabled: true,
          thresholdDb: a.compressor.threshold,
          ratio: a.compressor.ratio,
        }
      : null,
    reverb: a.reverb.enabled ? a.reverb.amount : 0,
  };
}

function processPeak(min: number, max: number, style: WaveformFxStyle): [number, number] {
  let lo = min * style.volume;
  let hi = max * style.volume;

  if (style.noiseRemoval > 0.001) {
    const thr = 0.015 + style.noiseRemoval * 0.14;
    const gate = (v: number) => {
      const a = Math.abs(v);
      if (a >= thr) return v;
      const t = a / thr;
      return v * t * t * (1 - style.noiseRemoval * 0.9);
    };
    lo = gate(lo);
    hi = gate(hi);
  }

  if (Math.abs(style.eqGainDb) > 0.01) {
    const g = Math.pow(10, style.eqGainDb / 20);
    lo *= g;
    hi *= g;
  }

  if (style.compressor) {
    const thr = Math.pow(10, style.compressor.thresholdDb / 20);
    const ratio = Math.max(1, style.compressor.ratio);
    const compress = (v: number) => {
      const a = Math.abs(v);
      if (a <= thr) return v;
      const shaped = thr + (a - thr) / ratio;
      return Math.sign(v) * shaped;
    };
    lo = compress(lo);
    hi = compress(hi);
  }

  // Soft display clip
  const soft = (v: number) => Math.tanh(v * 1.15);
  return [soft(lo), soft(hi)];
}

function paintPeaks(
  ctx: CanvasRenderingContext2D,
  data: PeakData,
  startBucket: number,
  span: number,
  w: number,
  h: number,
  color: string,
  style: WaveformFxStyle,
  ampScale: number,
  alpha: number
) {
  const mid = h / 2;
  ctx.fillStyle = color;
  ctx.globalAlpha = alpha;
  for (let x = 0; x < w; x++) {
    const b = startBucket + Math.floor((x / w) * span);
    if (b < 0 || b >= data.buckets) continue;
    let min = data.peaks[b * 2];
    let max = data.peaks[b * 2 + 1];
    [min, max] = processPeak(min, max, style);
    const y1 = mid + min * mid * 0.92 * ampScale;
    const y2 = mid + max * mid * 0.92 * ampScale;
    const top = Math.min(y1, y2);
    const bot = Math.max(y1, y2);
    ctx.fillRect(x, top, 1, Math.max(1, bot - top));
  }
  ctx.globalAlpha = 1;
}

/** Draw a segment of peaks [inPoint, inPoint+duration] into a canvas. */
export function drawWaveform(
  canvas: HTMLCanvasElement,
  data: PeakData,
  inPoint: number,
  duration: number,
  color: string,
  style?: WaveformFxStyle
) {
  const w = canvas.width;
  const h = canvas.height;
  const ctx = canvas.getContext('2d');
  if (!ctx || w < 1 || h < 1) return;

  const fx: WaveformFxStyle = style ?? {
    volume: 1,
    noiseRemoval: 0,
    eqGainDb: 0,
    compressor: null,
    reverb: 0,
  };

  ctx.clearRect(0, 0, w, h);
  const startRatio = Math.max(0, inPoint / data.duration);
  const endRatio = Math.min(1, (inPoint + duration) / data.duration);
  const startBucket = Math.floor(startRatio * data.buckets);
  const endBucket = Math.max(startBucket + 1, Math.ceil(endRatio * data.buckets));
  const span = endBucket - startBucket;

  // Reverb: soft wider ghost behind the dry waveform
  if (fx.reverb > 0.02) {
    paintPeaks(ctx, data, startBucket, span, w, h, color, fx, 1 + fx.reverb * 0.55, 0.18 + fx.reverb * 0.28);
  }

  paintPeaks(ctx, data, startBucket, span, w, h, color, fx, 1, 1);
}
